import { clientEnv } from '@/shared/config/env';

export const ANNOUNCE_DEPOSIT_TOPIC = 'announce.deposit';

export interface SiteRealtimeFrame {
  items?: unknown[];
  op: string;
  payload?: unknown;
  siteId?: string;
  topic?: string;
  v?: number;
}

export interface SiteRealtimeHandle {
  /** 已连接时追加订阅；重连后按连接时传入的 topics 与此后追加的一并重订。 */
  subscribe: (topics: string[]) => void;
  /** 记住当前场景；已鉴权连接立即上报，重连后自动补报。 */
  setScene: (scene: SiteRealtimeScene | null) => void;
  stop: () => void;
}

/** 玩家所在场景：游戏页带游戏编码，其余页面都算大厅。 */
export interface SiteRealtimeScene {
  gameCode?: string;
  scene: 'GAME' | 'LOBBY';
}

/** 服务端回写的子协议；带凭证时必须一并声明，否则浏览器拒绝握手。 */
export const SITE_REALTIME_PROTOCOL = 'fc-realtime';
const AUTH_PROTOCOL_PREFIX = 'fc-auth.';

const PING_MS = 20_000;
const BACKOFF_START_MS = 1_000;
const BACKOFF_MAX_MS = 15_000;

function normalizeApiBase(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

/** 各国 H5 共用。相对 /api 走当前页 origin；绝对地址改 ws/wss。 */
export function siteRealtimeUrl(siteId: string, apiBaseUrl = clientEnv.apiBaseUrl): string {
  const path = `${normalizeApiBase(apiBaseUrl)}/v1/app/ws`;
  const httpUrl = path.startsWith('http://') || path.startsWith('https://')
    ? new URL(path)
    : new URL(path, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
  httpUrl.protocol = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  httpUrl.searchParams.set('siteId', siteId);
  return httpUrl.toString();
}

function base64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * 登录凭证放子协议头而不是 URL：浏览器 WebSocket 不能带 Authorization，URL 又会进网关访问日志。
 * 未登录不带子协议，保持匿名站点广播连接。
 */
export function siteRealtimeProtocols(token?: string): string[] {
  const value = token?.trim();
  return value ? [SITE_REALTIME_PROTOCOL, `${AUTH_PROTOCOL_PREFIX}${base64Url(value)}`] : [];
}

export function sceneFrame(scene: SiteRealtimeScene): string {
  return JSON.stringify(
    scene.scene === 'GAME'
      ? { gameCode: scene.gameCode ?? '', op: 'scene', scene: 'GAME' }
      : { op: 'scene', scene: 'LOBBY' },
  );
}

export function parseSiteRealtimeFrame(raw: string): SiteRealtimeFrame | null {
  try {
    const value = JSON.parse(raw) as SiteRealtimeFrame;
    if (!value || typeof value.op !== 'string' || !value.op) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function connectSiteRealtime(options: {
  onFrame: (frame: SiteRealtimeFrame) => void;
  siteId: string;
  /** 玩家会话凭证；有则计入在线并可上报场景。 */
  token?: string;
  topics: string[];
}): SiteRealtimeHandle {
  const topics = new Set(options.topics);
  const protocols = siteRealtimeProtocols(options.token);
  let scene: SiteRealtimeScene | null = null;
  let socket: WebSocket | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = BACKOFF_START_MS;
  let stopped = false;

  const clearPing = () => {
    if (pingTimer != null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  };

  const clearReconnect = () => {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer != null) return;
    const wait = backoffMs;
    backoffMs = Math.min(BACKOFF_MAX_MS, backoffMs * 2);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      open();
    }, wait);
  };

  const open = () => {
    if (stopped || typeof WebSocket === 'undefined') return;
    clearReconnect();
    const next = protocols.length > 0
      ? new WebSocket(siteRealtimeUrl(options.siteId), protocols)
      : new WebSocket(siteRealtimeUrl(options.siteId));
    socket = next;
    next.onopen = () => {
      if (stopped) {
        next.close();
        return;
      }
      backoffMs = BACKOFF_START_MS;
      if (topics.size > 0) {
        next.send(JSON.stringify({ op: 'sub', topics: [...topics] }));
      }
      if (scene && protocols.length > 0) {
        next.send(sceneFrame(scene));
      }
      clearPing();
      pingTimer = setInterval(() => {
        if (next.readyState === WebSocket.OPEN) {
          next.send(JSON.stringify({ op: 'ping' }));
        }
      }, PING_MS);
    };
    next.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      const frame = parseSiteRealtimeFrame(event.data);
      if (frame) options.onFrame(frame);
    };
    next.onclose = () => {
      clearPing();
      if (socket === next) socket = null;
      scheduleReconnect();
    };
    next.onerror = () => {
      next.close();
    };
  };

  open();

  const sendIfOpen = (data: string) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(data);
    }
  };

  return {
    subscribe(next) {
      const added = next.filter((topic) => !topics.has(topic));
      if (added.length === 0) return;
      for (const topic of added) topics.add(topic);
      sendIfOpen(JSON.stringify({ op: 'sub', topics: added }));
    },
    setScene(next) {
      scene = next;
      if (next && protocols.length > 0) sendIfOpen(sceneFrame(next));
    },
    stop() {
      stopped = true;
      clearPing();
      clearReconnect();
      if (socket) {
        socket.onclose = null;
        socket.close();
        socket = null;
      }
    },
  };
}
